import type { ReactNode } from 'react';

import { Faq } from 'libs/http/api/content/content.types';

import { BankVerificationCard } from 'ui/molecules/BankVerificationCard/BankVerificationCard';
import { CompanyRecordCard } from 'ui/molecules/CompanyRecordCard/CompanyRecordCard';
import { OwnersRecordCard } from 'ui/molecules/OwnersRecordCard/OwnersRecordCard';
import { PepsScreeningCard } from 'ui/molecules/PepsScreeningCard/PepsScreeningCard';
import { MonitoringFeedBanner, type MonitoringFeedItem } from 'ui/molecules/MonitoringFeedBanner/MonitoringFeedBanner';
import type { RegisMessage } from 'ui/molecules/RegisChat/RegisChat.types';
import type { VerificationLayer } from 'ui/molecules/VerificationLayers/VerificationLayers.types';
import type { CoverageRow } from 'ui/organisms/CoverageSection/CoverageSection.types';
import type { SwitcherStep } from 'ui/organisms/StepSwitcherSection/StepSwitcherSection.types';

import styles from './BankAccountVerificationPage.module.scss';

type Translate = (key: string) => string;

// ── Hero proof ──────────────────────────────────────────────────────────────
export const getHeroStats = (t: Translate) => [
  { value: '49', label: t('countries for bank account verification') },
  { value: '600M+', label: t('companies you can verify') },
];

// ── One flow ─────────────────────────────────────────────────────────────────
const getMonitoringItems = (t: Translate): MonitoringFeedItem[] => [
  {
    company: 'Acme Trading Ltd — UBO Changed',
    parts: [{ text: t('UBO Changed'), bold: true }, { text: ' — J. Smith (75%) → R. Chen (75%)' }],
    tag: 'HIGH',
    tagVariant: 'rose',
    time: '15 Apr 2026',
  },
  {
    company: 'Acme Trading Ltd — Director Resigned',
    parts: [{ text: t('Director Resigned'), bold: true }, { text: t(' — M. Patel resigned as CFO') }],
    tag: 'MEDIUM',
    tagVariant: 'orange',
    time: '12 Apr 2026',
  },
  {
    company: 'Acme Trading Ltd — Annual Accounts Filed',
    parts: [
      { text: t('Annual Accounts Filed'), bold: true },
      { text: t(' — FY2025 accounts filed with Companies House') },
    ],
    tag: 'INFO',
    tagVariant: 'indigo',
    time: '10 Apr 2026',
  },
];

const renderBullets = (texts: ReactNode[]) => (
  <ul className={styles.flowBullets}>
    {texts.map((text, i) => (
      <li key={i} className={styles.flowBullet}>
        <span className={styles.flowBulletIco}>✓</span>
        <span className={styles.flowBulletText}>{text}</span>
      </li>
    ))}
  </ul>
);

export const getFlowSteps = (t: Translate): SwitcherStep[] => [
  {
    key: 'account',
    index: '01',
    tabTitle: t('Verify the bank account'),
    tabSub: t('Confirm ownership before you pay'),
    title: t('Verify the bank account.'),
    body: renderBullets([
      t('Validates the IBAN or account number — structure, checksum, bank routing'),
      t('Matches the payee name to the account holder, with a confidence score'),
      t("Confirms the account belongs to the business or person you're paying"),
    ]),
    card: <BankVerificationCard />,
  },
  {
    key: 'company',
    index: '02',
    tabTitle: t('Verify the company'),
    tabSub: t('The legal entity behind the account'),
    title: t('Verify the company.'),
    body: renderBullets([
      t('Pulls the legal name, registration number and live status from the official registry'),
      t('Validates VAT and registered address across jurisdictions'),
      t('Returns up to 20 years of filed financials — balance sheet, P&L, ratios'),
    ]),
    card: <CompanyRecordCard />,
  },
  {
    key: 'owners',
    index: '03',
    tabTitle: t('Verify the owners'),
    tabSub: t('UBOs, directors and group structure'),
    title: t('Verify the owners.'),
    body: renderBullets([
      t('Identifies UBOs with exact ownership percentages, from shareholder filings'),
      t('Lists directors and officers — current and historical'),
      t('Maps the group structure — parent companies, subsidiaries, full corporate tree'),
    ]),
    card: <OwnersRecordCard />,
  },
  {
    key: 'peps',
    index: '04',
    tabTitle: t('Screen for PEPs & sanctions'),
    tabSub: t('The people behind the entity'),
    title: t('Screen for PEPs & sanctions.'),
    body: renderBullets([
      t('Screens every UBO, director and shareholder — not just the company name'),
      t('Checks global sanctions and PEP lists — OFAC, EU, UN, HMT and more'),
      t('Resolves matches against the registry identity to cut false positives'),
    ]),
    card: <PepsScreeningCard />,
  },
  {
    key: 'monitor',
    index: '05',
    tabTitle: t('Monitor for changes'),
    tabSub: t('Alerts before your next payment run'),
    title: t('Monitor for changes.'),
    body: renderBullets([
      t('Tracks ownership changes — new shareholders, UBO updates, director resignations'),
      t('Tracks account status — dormant accounts, closures, IBAN deactivations'),
      t('Tracks company changes — status updates, address moves, financial filings'),
      t('Delivers alerts by webhook, straight to your system'),
    ]),
    card: <MonitoringFeedBanner items={getMonitoringItems(t)} />,
  },
];

// ── Anatomy layers ───────────────────────────────────────────────────────────
export const getLayers = (t: Translate): VerificationLayer[] => [
  {
    tag: t('Layer 01 · Account'),
    title: t("The account is valid — and it's theirs"),
    items: [
      t('IBAN & account structure validation'),
      t('Payee name matched to the account holder'),
      t('Verified via bank schemes and partners — SCAN, VoP and more'),
    ],
  },
  {
    tag: t('Layer 02 · Entity'),
    title: t("The holder is the company you're paying"),
    items: [
      t('Payee name matched to registered legal name'),
      t('Live entity status — active, dissolved, in liquidation'),
      t('Company number, VAT & LEI cross-referenced'),
    ],
  },
  {
    tag: t('Layer 03 · Ownership'),
    title: t('The company is who it says it is'),
    items: [
      t('UBOs and directors on the same record'),
      t('Group structure and parent entities'),
      t('Sourced first-party from 400+ government registries'),
    ],
  },
];

// ── Red flags ────────────────────────────────────────────────────────────────
export const getRiskQuestions = (t: Translate): string[] => [
  t('Was the company registered only weeks ago?'),
  t('Did the ownership change in the last month?'),
  t('Is it registered at an address shared with hundreds of other companies?'),
  t('Has it been filing financials with the local authority on schedule?'),
  t('Are the company, its directors or shareholders on PEP or sanctions lists?'),
];

// ── Outcomes ─────────────────────────────────────────────────────────────────
// Result codes (MATCH, CLOSE_MATCH…) are technical labels — left in English.
export const getOutcomes = (t: Translate): CoverageRow[] => [
  { dot: 'green', label: 'MATCH', description: t('Release the payment. Full audit trail attached.') },
  {
    dot: 'amber',
    label: 'CLOSE_MATCH',
    description: t('Routed to review with reason codes — never silent auto-approval.'),
  },
  { dot: 'red', label: 'NO_MATCH', description: t('Stop and investigate before funds move.') },
  {
    dot: 'neutral',
    label: 'UNABLE_TO_VERIFY',
    description: t('Scheme coverage unavailable — the entity is still verified against the registry record.'),
  },
];

// ── Regis conversation ───────────────────────────────────────────────────────
export const getRegisMessages = (t: Translate): RegisMessage[] => [
  {
    from: 'user',
    content: t('Verify GB29 NWBK 6016 1331 9268 19 for Acme Trading Ltd'),
  },
  {
    from: 'assistant',
    content: (
      <>
        {t('Done — verified in 187ms:')} <b>{t('account ownership confirmed')}</b>
        {t(
          ', name match exact, entity active (incorporated 2016). Want me to pull the UBOs and screen them against sanctions lists?',
        )}
      </>
    ),
  },
];

// ── FAQ ──────────────────────────────────────────────────────────────────────
export const getFaqItems = (t: Translate): Faq[] => [
  {
    id: 'faq-1',
    default_opened: true,
    question: t('What is bank account verification?'),
    answer: t(
      'It confirms that a bank account exists and belongs to the person or company you intend to pay — before the money moves. We validate the account through trusted payment schemes and banking partners, then cross-reference the payee against official government registries to confirm legal status, corporate structure and ownership.',
    ),
  },
  {
    id: 'faq-2',
    question: t('How is this different from IBAN validation?'),
    answer: t(
      "IBAN validation only checks that an account number is correctly formatted and exists. Verification goes further: it matches the account holder's name against the name you provide, and confirms the legal entity behind the account in the government registry. A fraudster can pass an IBAN check; passing a name-and-entity match is much harder.",
    ),
  },
  {
    id: 'faq-3',
    question: t('Which countries are covered?'),
    answer: t(
      '49 markets. Direct bank account verification via local banking infrastructure in 21 countries — including the United States, Canada, Brazil, India, China and Singapore — and full European Union plus United Kingdom coverage via the Pay.UK and European Payments Council Verification of Payee schemes.',
    ),
  },
  {
    id: 'faq-4',
    question: t('What do I need to run a verification?'),
    answer: t(
      'Three things: the country, the account number or IBAN, and the account holder name you expect. The result comes back in seconds, with the registry record of the company attached.',
    ),
  },
  {
    id: 'faq-5',
    question: t('What results can a verification return?'),
    answer: t(
      'Exactly one of four outcomes: MATCH (release the payment), CLOSE_MATCH (routed to review with reason codes), NO_MATCH (stop and investigate), or UNABLE_TO_VERIFY (scheme coverage unavailable — the entity is still verified against the registry record). No ambiguous scores, no silent approvals.',
    ),
  },
  {
    id: 'faq-6',
    question: t('What happens after the account check passes?'),
    answer: t(
      "The verification doesn't stop at the account. The same call cross-references the payee against government registries: legal status, incorporation date, registered address, filing history, shareholders and ultimate beneficial owners, plus PEP and sanctions screening of the people behind the entity.",
    ),
  },
  {
    id: 'faq-7',
    question: t('Can I verify accounts in bulk?'),
    answer: t(
      'Yes. Upload a CSV to the web platform — 2,000 accounts verified in one run — or use the API for continuous verification inside your payment flow, supplier onboarding or AP system.',
    ),
  },
  {
    id: 'faq-8',
    question: t('How do I integrate it?'),
    answer: t(
      'Four ways: the web platform (no integration needed), the REST API and MCP server, Regis — our AI analyst you can ask in plain language — or the free connectors for Claude, ChatGPT and Cursor.',
    ),
  },
  {
    id: 'faq-9',
    question: t('Where does the company data come from?'),
    answer: t(
      'Directly from official government registries — over 400 registries across 200+ countries. Every data point carries source attribution: the registry name, a link to the underlying filing, and the retrieval timestamp. Nothing is scraped from the open web.',
    ),
  },
  {
    id: 'faq-10',
    question: t('How much does it cost?'),
    answer: t(
      'You pay per verification, with volume pricing at scale. You can start free — request a trial of the web platform or get a free API key at globaldatabase.com/mcp and test in the sandbox before committing.',
    ),
  },
];
